/**
 * Maps backend settings to component CSS variables.
 * This provides a layer of abstraction between backend CSS variable names (--ss-calendar-events-*)
 * and component CSS variable names (--event-*), keeping the CalendarEvent component generic and reusable.
 */
export class CalendarEventCssMapper {
    private cssVariables: Record<string, string>;

    constructor(cssVariables: Record<string, string>) {
        this.cssVariables = cssVariables;
    }

    /**
     * Maps backend settings to component CSS variables.
     * Component uses generic CSS variable names (--event-*) for reusability.
     * Backend uses prefixed names (--ss-calendar-events-*) for specificity.
     */
    private static readonly cssVarMap: Array<{
            componentVar: string;
            backendVar: string;
            defaultValue: string;
        }> = [
            { componentVar: '--event-date-color', backendVar: '--ss-calendar-events-event-date-color', defaultValue: 'inherit' },
            { componentVar: '--event-date-bg-color', backendVar: '--ss-calendar-events-event-date-bg-color', defaultValue: 'inherit' },
            { componentVar: '--event-time-color', backendVar: '--ss-calendar-events-event-time-color', defaultValue: 'inherit' },
            { componentVar: '--event-info-font-family', backendVar: '--ss-calendar-events-event-title-font-family', defaultValue: 'inherit' },
            { componentVar: '--event-info-font-weight', backendVar: '--ss-calendar-events-event-title-font-weight', defaultValue: 'normal' },
            { componentVar: '--event-info-font-style', backendVar: '--ss-calendar-events-event-title-font-style', defaultValue: 'normal' },
            { componentVar: '--event-info-color', backendVar: '--ss-calendar-events-event-content-color', defaultValue: 'inherit' },
            { componentVar: '--event-date-font-family', backendVar: '--ss-calendar-events-event-date-font-family', defaultValue: 'inherit' },
            { componentVar: '--event-date-font-weight', backendVar: '--ss-calendar-events-event-date-font-weight', defaultValue: 'normal' },
            { componentVar: '--event-date-font-style', backendVar: '--ss-calendar-events-event-date-font-style', defaultValue: 'normal' },
        ];

    public getComponentCssVariables(): Record<string, string> {
        const result: Record<string, string> = {};
        for (const { componentVar, backendVar, defaultValue } of CalendarEventCssMapper.cssVarMap) {
            if (componentVar === '--event-time-color') {
                const gridOverride = this.cssVariables['--ss-calendar-events-grid-event-details-time-color'];
                if (gridOverride) {
                    result[componentVar] = gridOverride;
                    continue;
                }
            }
            result[componentVar] = this.cssVariables[backendVar] ?? defaultValue;
        }
        return result;
    }

    /**
     * Updates the settings and returns new CSS variables.
     * Useful for runtime theme changes.
     */
    updateCssVariables(newCssVariables: Partial<Record<string, string>>): Record<string, string> {
        this.cssVariables = { ...this.cssVariables, ...newCssVariables } as Record<string, string>;
        return this.getComponentCssVariables();
    }
}
